// Notificações (canvas E08): pedido de amizade com Aceitar e Recusar, pedido aceito, chamado para a mesa, convite
// de trio e as da comunidade (convite, evento que vai começar e o aviso de só leitura), agrupadas em hoje, esta semana
// e antes. "Ler todas" zera o sino do Início. No computador, o sino abre as notificações num painel à direita, por
// cima do Início.
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ErroDaApi } from '../comum/api.ts';
import { aceitarConviteDeTrio, lerNotificacoes, lerTodasAsNotificacoes, type Notificacao, recusarConviteDeTrio } from '../comum/amigos.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { BotaoPerfil } from '../comum/BotaoPerfil.tsx';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { atualizarPresenca } from '../comum/presenca.ts';
import { grupoDe, quandoFoi } from '../comum/quando.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { irParaMesa } from '../comum/limite.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { quandoDoEvento } from '../comum/comunidades.ts';
import { Escudo, VerPlanos } from '../liga/trios.tsx';
import { Tela, Topo } from './comum.tsx';

const GRUPOS = [['hoje', 'HOJE'], ['semana', 'ESTA SEMANA'], ['antes', 'ANTES']] as const;

export function Notificacoes({ sessao, perfil, painel = false }: { sessao: Sessao; perfil: PerfilConta; painel?: boolean }) {
  const [lista, setLista] = useState<Notificacao[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerNotificacoes(sessao).then((r) => { if (vivo) setLista(r.notificacoes); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  const agora = Date.now();
  const novas = lista?.filter((n) => !n.lida).length ?? 0;
  async function lerTodas() {
    await lerTodasAsNotificacoes(sessao).catch(() => {});
    setLista((l) => l?.map((n) => ({ ...n, lida: true })) ?? null);
    atualizarPresenca();
  }
  const lerTodasBotao = novas > 0 && <button className="link" onClick={() => void lerTodas()}>Ler todas</button>;
  const corpo: ReactNode = (
    <>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!lista && !erro && <p className="carregando" role="status">Carregando...</p>}
      {lista && lista.length === 0 && <p className="cartao vazio-lista">Nenhuma notificação por enquanto. Pedidos de amizade, chamados para a mesa, convites de trio e avisos das suas comunidades e dos seus campeonatos aparecem aqui.</p>}
      {lista && GRUPOS.map(([g, titulo]) => {
        const doGrupo = lista.filter((n) => grupoDe(agora, n.criadaEm) === g);
        if (!doGrupo.length) return null;
        const novasNoGrupo = doGrupo.filter((n) => !n.lida).length;
        return (
          <section key={g} className="grupo-notificacoes" aria-label={titulo}>
            <h2 className="rotulo">{titulo}{novasNoGrupo > 0 && <span className="dourado"> · {novasNoGrupo} {novasNoGrupo === 1 ? 'NOVA' : 'NOVAS'}</span>}</h2>
            <ul className="lista-notificacoes">
              {doGrupo.map((n) => (
                <Item key={n.id} n={n} agora={agora} sessao={sessao} perfil={perfil} aoSair={() => setLista((l) => l?.filter((x) => x.id !== n.id) ?? null)} />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
  if (painel) return <PainelDoSino lerTodas={lerTodasBotao}>{corpo}</PainelDoSino>;
  return (
    <Tela>
      <div className="topo-com-acao">
        <Topo titulo="Notificações" voltar={voltar} />
        {lerTodasBotao}
      </div>
      {corpo}
    </Tela>
  );
}

// O painel do sino no computador: à direita, por cima do Início. Fecha no ×, no Esc e no clique fora dele.
function PainelDoSino({ lerTodas, children }: { lerTodas: ReactNode; children: ReactNode }) {
  useEffect(() => {
    const f = (ev: KeyboardEvent) => { if (ev.key === 'Escape') voltar(); };
    addEventListener('keydown', f);
    return () => removeEventListener('keydown', f);
  }, []);
  return createPortal(
    <div className="perfil-fundo painel-sino" onClick={(ev) => { if (ev.target === ev.currentTarget) voltar(); }}>
      <section className="perfil-folha" role="dialog" aria-modal="true" aria-labelledby="titulo-notificacoes">
        <div className="topo-com-acao">
          <h1 id="titulo-notificacoes" className="titulo-painel">Notificações</h1>
          {lerTodas}
          <button className="fechar-folha" aria-label="Fechar notificações" autoFocus onClick={voltar}>×</button>
        </div>
        {children}
      </section>
    </div>,
    document.body,
  );
}

function Item({ n, agora, sessao, perfil, aoSair }: { n: Notificacao; agora: number; sessao: Sessao; perfil: PerfilConta; aoSair: () => void }) {
  const quem = n.de?.apelido ?? 'Alguém';
  const nome = typeof n.dados.nome === 'string' ? n.dados.nome : '';
  let texto: React.ReactNode, acao: React.ReactNode = null;
  if (n.tipo === 'amizade_pedido') {
    texto = <><b>{quem}</b> quer ser seu amigo</>;
    acao = n.de && <BotaoAmizade usuario={n.de.usuario} apelido={quem} compacto />;
  } else if (n.tipo === 'amizade_aceita') {
    texto = <><b>{quem}</b> aceitou seu pedido</>;
  } else if (n.tipo === 'chamado_mesa') {
    texto = <><b>{quem}</b> chamou você para a <b>{nome || 'mesa'}</b></>;
    const codigo = typeof n.dados.codigo === 'string' ? n.dados.codigo : null;
    acao = codigo && <button className="btn2 entrar" onClick={() => irParaMesa({ tela: 'sala', codigo, vaga: 'amigo' })}>Entrar</button>;
  } else if (n.tipo === 'comunidade_convite') {
    texto = <><b>{quem}</b> chamou você para a comunidade <b>{nome}</b></>;
    const convite = typeof n.dados.convite === 'string' ? n.dados.convite : null;
    acao = convite && <button className="btn2 entrar" onClick={() => ir({ tela: 'convite-comunidade', codigo: convite })}>Ver</button>;
  } else if (n.tipo === 'comunidade_evento' || n.tipo === 'comunidade_aviso') {
    const comunidade = typeof n.dados.comunidade === 'string' ? n.dados.comunidade : null;
    const titulo = typeof n.dados.titulo === 'string' ? n.dados.titulo : 'Evento';
    texto = n.tipo === 'comunidade_aviso'
      ? <><b>{nome}</b> fica só para leitura em {String(n.dados.dias ?? 30)} dias. O dono precisa voltar ao Ouro ou passar a comunidade a um moderador com Ouro.</>
      : <><b>{titulo}</b> {n.dados.antes === 'hora' ? 'começa em 1 hora' : 'é amanhã'}, na <b>{nome}</b>{typeof n.dados.quando === 'number' ? ` · ${quandoDoEvento(n.dados.quando)}` : ''}</>;
    acao = comunidade && <button className="btn2 entrar" onClick={() => ir({ tela: 'comunidade', id: comunidade, aba: n.tipo === 'comunidade_evento' ? 'eventos' : undefined })}>Ver</button>;
  } else if (n.tipo === 'campeonato_convite' || n.tipo === 'campeonato_aviso' || n.tipo === 'campeonato_jogo') {
    const slug = typeof n.dados.slug === 'string' ? n.dados.slug : null, dito = typeof n.dados.texto === 'string' ? n.dados.texto : '';
    const motivo = typeof n.dados.motivo === 'string' && n.dados.motivo ? n.dados.motivo : null;
    texto = n.tipo === 'campeonato_convite' ? <><b>{quem}</b> chamou você para o campeonato <b>{nome}</b></>
      : n.tipo === 'campeonato_aviso' ? <><b>{nome}</b>: {dito}</>
        : <><b>{nome}</b>: {dito}{motivo && <> Motivo: {motivo}</>}</>;
    acao = slug && <button className="btn2 entrar" onClick={() => ir({ tela: 'campeonato', slug, ...(n.tipo === 'campeonato_jogo' ? { aba: 'jogos' as const } : {}) })}>Ver</button>;
  } else {
    texto = <><b>{quem}</b> chamou você para o trio <b>{nome}</b></>;
    const convite = typeof n.dados.convite === 'string' ? n.dados.convite : null;
    acao = convite && <RespostaDoTrio sessao={sessao} perfil={perfil} convite={convite} nome={nome} aoSair={aoSair} />;
  }
  return (
    <li className={[n.lida ? '' : 'nova', n.tipo === 'convite_trio' ? 'com-resposta' : ''].filter(Boolean).join(' ') || undefined}>
      <span className="rosto-notificacao">
        {n.de ? (
          <BotaoPerfil usuario={n.de.usuario} apelido={n.de.apelido}>
            <Rosto perfil={{ apelido: n.de.apelido, avatar: n.de.avatar }} usuario={n.de.usuario} cor="var(--gold-600)" tamanho={44} />
          </BotaoPerfil>
        ) : typeof n.dados.escudo === 'string' ? <Escudo id={n.dados.escudo} tamanho={38} />
          : n.tipo.startsWith('campeonato_') ? <span className="trofeu" style={{ width: 44, height: 44, fontSize: 22 }} aria-hidden="true">🏆</span>
            : <Rosto perfil={null} cor="var(--gold-600)" tamanho={44} />}
        {!n.lida && <span className="ponto-novo" role="img" aria-label="Nova" />}
      </span>
      <span className="texto-notificacao"><span>{texto}</span><small>{quandoFoi(agora, n.criadaEm)}</small></span>
      {acao}
    </li>
  );
}

// Convite de trio: Entrar usa o limite do plano de quem entra. No limite, aparece "Seu plano permite N trios" com o
// atalho para Planos, e o convite fica guardado até vencer. Recusar é silencioso para quem chamou.
function RespostaDoTrio({ sessao, perfil, convite, nome, aoSair }: { sessao: Sessao; perfil: PerfilConta; convite: string; nome: string; aoSair: () => void }) {
  const [ocupado, setOcupado] = useState(false);
  const [limite, setLimite] = useState<string | null>(null);
  async function entrar() {
    if (ocupado) return;
    setOcupado(true);
    try {
      const r = await aceitarConviteDeTrio(sessao, convite, { apelido: perfil.apelido, avatar: perfil.avatar });
      medir('trio_convite_aceito');
      avisarNaTela(`Você entrou no trio ${r.trio.nome}.`);
      aoSair();
      ir({ tela: 'trio', id: r.trio.id });
    } catch (e) {
      if (e instanceof ErroDaApi && e.codigo === 'limite') setLimite(e.message);
      else {
        avisarNaTela(e instanceof Error ? e.message : 'Não deu certo. Tente de novo.');
        if (e instanceof ErroDaApi && e.status < 500) aoSair();
      }
    } finally { setOcupado(false); }
  }
  async function recusar() {
    if (ocupado) return;
    setOcupado(true);
    try { await recusarConviteDeTrio(sessao, convite); } catch { /* vencido ou já respondido: sai da lista do mesmo jeito */ }
    aoSair();
  }
  return (
    <span className="resposta-trio">
      <button className="btn2 entrar" disabled={ocupado} aria-label={`Entrar no trio ${nome}`} onClick={() => void entrar()}>Entrar</button>
      <button className="btn2 recusar-trio" disabled={ocupado} aria-label={`Recusar o convite do trio ${nome}`} onClick={() => void recusar()}>Recusar</button>
      {limite && createPortal(
        <div className="dialogo-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) setLimite(null); }}>
          <div className="dialogo" role="alertdialog" aria-modal="true" aria-labelledby="trio-limite">
            <h2 id="trio-limite">{limite}</h2>
            <p>O convite para o trio {nome} fica guardado até vencer. Saia de um trio ou mude de plano para entrar.</p>
            <VerPlanos />
            <button className="btn2 largo" onClick={() => setLimite(null)}>Agora não</button>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
